import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Save,
  User as UserIcon,
  Camera,
  Mail,
  Phone,
  Building2,
  UserRound,
  X,
} from "lucide-react";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { useAuth } from "@/lib/auth";
import { sharedApi } from "@/lib/api";

/**
 * Iter-274f W2 — Edit "My Account" without going through email OTP.
 *
 * All updates flow through the same `PATCH /auth/me` endpoint the mobile
 * pod switched to in Iter-274 §W4. The backend already treats these
 * fields as NON-SENSITIVE and does NOT trigger email/phone re-verification:
 *   - full_name, picture, account_type
 *   - business.name / .nzbn / .phone / .address (when account_type=business)
 *
 * Email is view-only here — changing an email genuinely IS sensitive
 * and must stay on the existing verify-email flow (Mobile pod owns it).
 * Phone number is view-only too — a real change flips `phone_verified`
 * back to false and needs the SMS verify flow, which lives on Profile
 * once Twilio creds land.
 *
 * Photo: base64 data-URI, <= 2 MB, JPEG/PNG/WebP only. Anything else is
 * rejected client-side so the user gets an instant error instead of a
 * 413 round-trip.
 */
const MAX_PHOTO_BYTES = 2 * 1024 * 1024;
const OK_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];

function errMsg(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return err?.message || fallback;
}

export default function AccountEditPage() {
  const { user, patchMe } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    full_name: user?.full_name || user?.name || "",
    picture: user?.picture || "",
    account_type: user?.account_type || "individual",
    business_name: user?.business?.name || user?.business_name || "",
    business_nzbn: user?.business?.nzbn || user?.business_nzbn || "",
    business_phone: user?.business?.phone || user?.business_phone || "",
    business_address: user?.business?.address || user?.business_address || "",
  });
  const [nzbnError, setNzbnError] = useState("");
  const [pendingPhoto, setPendingPhoto] = useState(null); // base64 data-URI if newly picked
  const fileRef = useRef(null);

  // If the auth context loads after mount, refresh once from the user.
  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      full_name: f.full_name || user.full_name || user.name || "",
      picture: f.picture || user.picture || "",
      account_type: f.account_type || user.account_type || "individual",
      business_name: f.business_name || user.business?.name || user.business_name || "",
      business_nzbn: f.business_nzbn || user.business?.nzbn || user.business_nzbn || "",
      business_phone: f.business_phone || user.business?.phone || user.business_phone || "",
      business_address: f.business_address || user.business?.address || user.business_address || "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const isBusiness = form.account_type === "business";

  const onPhotoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!OK_PHOTO_TYPES.includes(file.type)) {
      toast.error("Photo must be JPEG, PNG or WebP.");
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error("Photo must be under 2 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPendingPhoto(String(reader.result));
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const removePhoto = () => {
    setPendingPhoto("");
    setForm((f) => ({ ...f, picture: "" }));
  };

  const save = useMutation({
    mutationFn: async () => {
      // Iter-274 W6 write-boundary: whitelist. Nothing server-owned is
      // echoed back (no user_id / updated_at / verified flags).
      const patch = {
        full_name: form.full_name.trim(),
        account_type: form.account_type,
      };
      if (pendingPhoto !== null) {
        // Iter-274f contract-fix (backend feedback iter-97): the shared
        // backend requires an https URL for `picture`, not the raw
        // data-URI. Round-trip through /uploads/image first, then send
        // the returned URL. Empty string / null clears the picture.
        if (pendingPhoto) {
          const { data } = await sharedApi.post("/uploads/image", { data: pendingPhoto });
          patch.picture = data?.url || null;
        } else {
          patch.picture = null;
        }
      }
      if (isBusiness) {
        patch.business_name = form.business_name.trim();
        patch.business_nzbn = form.business_nzbn ? form.business_nzbn.replace(/\D/g, "") : "";
        patch.business_phone = form.business_phone || "";
        patch.business_address = form.business_address || "";
      }
      return patchMe(patch);
    },
    onSuccess: () => {
      toast.success("Profile updated.");
      nav("/account", { replace: true });
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && /nzbn/i.test(detail)) {
        setNzbnError(detail);
      }
      toast.error(errMsg(err, "Save failed"));
    },
  });

  const onSubmit = (e) => {
    e.preventDefault();
    setNzbnError("");
    if (!form.full_name.trim()) {
      toast.error("Please enter your name.");
      return;
    }
    if (isBusiness) {
      if (!form.business_name.trim()) {
        toast.error("Business name is required.");
        return;
      }
      // Iter-290 §2 — NZBN, phone, address are mandatory for business
      // accounts (backend enforces).
      const digits = (form.business_nzbn || "").replace(/\D/g, "");
      if (digits.length !== 13) {
        setNzbnError("NZBN is required — must be 13 digits.");
        return;
      }
      if (!(form.business_phone || "").trim()) {
        toast.error("Business phone is required.");
        return;
      }
      if (!(form.business_address || "").trim()) {
        toast.error("Business address is required.");
        return;
      }
    }
    save.mutate();
  };

  const previewSrc = pendingPhoto || form.picture || "";

  return (
    <div className="max-w-2xl mx-auto p-6" data-testid="account-edit-page">
      <Seo title="Edit account · Allsale Classified" />
      <Breadcrumbs
        items={[
          { name: "Home", href: "/" },
          { name: "Account", href: "/account" },
          { name: "Edit" },
        ]}
      />

      <Link
        to="/account"
        className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-[color:var(--brand-blue)]"
        data-testid="account-edit-back"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to My account
      </Link>

      <h1 className="mt-3 font-display text-3xl md:text-4xl font-bold text-slate-900">
        Edit profile
      </h1>
      <p className="mt-1 text-sm text-slate-500">
        Name, photo and business details save instantly — no email code required.
      </p>

      <form onSubmit={onSubmit} className="mt-6 space-y-6" data-testid="account-edit-form">
        {/* Iter-600 §3 — gold wallet shortcut on the profile editor */}
        <Link
          to="/rewards"
          data-testid="account-edit-wallet-btn"
          className="flex items-center justify-center gap-2 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 text-white font-bold py-3 text-sm shadow-card hover:opacity-95 transition-opacity"
        >
          🪙 My Wallet — Reward Coins
        </Link>

        {/* Photo */}
        <section className="rounded-3xl bg-white shadow-card p-6" data-testid="account-edit-photo-card">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">Profile photo</div>
          <div className="flex items-center gap-4">
            {previewSrc ? (
              <img
                src={previewSrc}
                alt=""
                data-testid="account-edit-photo-preview"
                className="w-20 h-20 rounded-full object-cover ring-2 ring-slate-200"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-slate-100 grid place-items-center text-slate-400">
                <UserIcon className="w-8 h-8" />
              </div>
            )}
            <div className="flex-1 min-w-0 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="account-edit-photo-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue)] hover:text-white font-semibold px-4 py-1.5 text-xs transition-colors"
              >
                <Camera className="w-3.5 h-3.5" /> Choose photo
              </button>
              {previewSrc && (
                <button
                  type="button"
                  onClick={removePhoto}
                  data-testid="account-edit-photo-remove"
                  className="inline-flex items-center gap-1.5 rounded-full bg-white border border-red-200 text-red-500 hover:bg-red-50 font-semibold px-4 py-1.5 text-xs transition-colors"
                >
                  <X className="w-3.5 h-3.5" /> Remove
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={onPhotoPick}
                className="hidden"
                data-testid="account-edit-photo-input"
              />
            </div>
          </div>
          <p className="mt-2 text-[11px] text-slate-400">JPEG / PNG / WebP up to 2 MB.</p>
        </section>

        {/* Basic fields */}
        <section className="rounded-3xl bg-white shadow-card p-6 space-y-4" data-testid="account-edit-basic-card">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Basic details</div>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">Full name</span>
            <input
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              data-testid="account-edit-full-name"
              required
              className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm"
              placeholder="Jane Doe"
            />
          </label>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="min-w-0 rounded-xl border border-soft bg-slate-50 px-3 py-2.5 text-sm">
              <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 block">Email</span>
              <span className="flex items-center gap-1.5 text-slate-700 min-w-0" data-testid="account-edit-email-readonly">
                <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span className="break-all">{user?.email || "—"}</span>
              </span>
              <p className="text-[10px] text-slate-400 mt-1">To change your email, contact support.</p>
            </div>
            <div className="min-w-0 rounded-xl border border-soft bg-slate-50 px-3 py-2.5 text-sm">
              <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 block">Phone</span>
              <span className="flex items-center gap-1.5 text-slate-700 min-w-0" data-testid="account-edit-phone-readonly">
                <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span className="break-all">{user?.phone || "Not set"}</span>
              </span>
              <p className="text-[10px] text-slate-400 mt-1">Add or verify your phone from Profile.</p>
            </div>
          </div>
        </section>

        {/* Account type toggle */}
        <section className="rounded-3xl bg-white shadow-card p-6" data-testid="account-edit-type-card">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">Account type</div>
          <div className="grid grid-cols-2 gap-2 p-1 rounded-2xl bg-slate-100" data-testid="account-edit-account-type">
            <button
              type="button"
              onClick={() => setForm({ ...form, account_type: "individual" })}
              data-testid="account-edit-type-individual"
              className={`inline-flex items-center justify-center gap-2 rounded-xl py-2 text-sm font-semibold transition-colors ${!isBusiness ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-800"}`}
            >
              <UserRound className="w-4 h-4" /> Individual
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, account_type: "business" })}
              data-testid="account-edit-type-business"
              className={`inline-flex items-center justify-center gap-2 rounded-xl py-2 text-sm font-semibold transition-colors ${isBusiness ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-800"}`}
            >
              <Building2 className="w-4 h-4" /> Business
            </button>
          </div>
          <p className="mt-2 text-[11px] text-slate-400">
            {isBusiness
              ? "Your listings show your business name and a 🏢 Business badge."
              : "You can switch to a Business account any time — no re-verification needed."}
          </p>
        </section>

        {isBusiness && (
          <section className="rounded-3xl bg-white shadow-card p-6 space-y-4" data-testid="account-edit-business-card">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Business details</div>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">
                Business name <span className="text-red-500">*</span>
              </span>
              <input
                value={form.business_name}
                onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                data-testid="account-edit-business-name"
                required
                className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm"
                placeholder="Patel Motors Ltd"
              />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">NZBN *</span>
              <input
                value={form.business_nzbn}
                onChange={(e) => {
                  setForm({ ...form, business_nzbn: e.target.value });
                  if (nzbnError) setNzbnError("");
                }}
                data-testid="account-edit-business-nzbn"
                inputMode="numeric"
                required
                maxLength={13}
                className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm"
                placeholder="9429000000000"
              />
              {nzbnError && (
                <p className="mt-1 text-xs text-red-500" data-testid="account-edit-business-nzbn-error">
                  {nzbnError}
                </p>
              )}
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">Business phone *</span>
              <input
                value={form.business_phone}
                onChange={(e) => setForm({ ...form, business_phone: e.target.value })}
                data-testid="account-edit-business-phone"
                inputMode="tel"
                required
                className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm"
                placeholder="09 555 0100"
              />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">Business address *</span>
              <input
                value={form.business_address}
                onChange={(e) => setForm({ ...form, business_address: e.target.value })}
                data-testid="account-edit-business-address"
                required
                className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm"
                placeholder="123 Queen Street, Auckland"
              />
            </label>
          </section>
        )}

        <div className="flex items-center justify-end gap-3">
          <Link
            to="/account"
            className="text-sm font-semibold text-slate-500 hover:text-slate-800"
            data-testid="account-edit-cancel"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={save.isPending}
            data-testid="account-edit-save"
            className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-5 py-2.5 disabled:opacity-70 transition-colors"
          >
            {save.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save changes
          </button>
        </div>
      </form>
    </div>
  );
}
